import * as React from "react";

/**
 * SEOHead - Dynamic SEO meta tags component
 * Updates document head with SEO-friendly meta tags using vanilla JS
 */
export function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = "website",
  noIndex = false,
  noFollow = false,
  googleAnalytics,
  googleVerification,
  children,
}) {
  React.useEffect(() => {
    // Update document title
    if (title) {
      document.title = title;
    }

    // Update or create meta tags
    const updateMeta = (name, content, property = false) => {
      if (!content) return;
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Primary meta tags
    if (description) updateMeta('description', description);
    if (keywords) updateMeta('keywords', keywords);
    
    // Robots
    const robotsContent = [
      noIndex ? "noindex" : "index",
      noFollow ? "nofollow" : "follow",
    ].join(", ");
    updateMeta('robots', robotsContent);

    // Google verification
    if (googleVerification) updateMeta('google-site-verification', googleVerification);

    // Open Graph
    if (title) updateMeta('og:title', title, true);
    if (description) updateMeta('og:description', description, true);
    updateMeta('og:type', ogType, true);
    if (canonicalUrl) updateMeta('og:url', canonicalUrl, true);
    if (ogImage) updateMeta('og:image', ogImage, true);

    // Twitter
    updateMeta('twitter:card', 'summary_large_image');
    if (title) updateMeta('twitter:title', title);
    if (description) updateMeta('twitter:description', description);
    if (ogImage) updateMeta('twitter:image', ogImage);

    // Canonical link
    if (canonicalUrl) {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalUrl);
    }

    // Google Analytics
    if (googleAnalytics && googleAnalytics.startsWith('G-') && !window.__GA_INITIALIZED__) {
      window.__GA_INITIALIZED__ = true;
      const gaId = googleAnalytics.split(':')[0].trim();
      if (gaId && gaId.startsWith('G-')) {
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
        document.head.appendChild(script);
        
        window.dataLayer = window.dataLayer || [];
        function gtag(){window.dataLayer.push(arguments);}
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', gaId);
      }
    }
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, noIndex, noFollow, googleAnalytics, googleVerification]);

  return null;
}

/**
 * useSEO - Hook to fetch and use SEO settings
 */
export function useSEO() {
  const [seo, setSeo] = React.useState(null);

  React.useEffect(() => {
    const fetchSEO = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || "";
        const res = await fetch(`${API_URL}/api/seo`);
        if (res.ok) {
          const data = await res.json();
          setSeo(data);
        }
      } catch (err) {
        console.error("Failed to fetch SEO settings:", err);
      }
    };
    fetchSEO();
  }, []);

  return seo;
}

export default SEOHead;
